import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Panel } from "@/components/panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { DecoderPanel } from "@/features/decoder";
import { WatchlistPanel } from "@/features/watchlist/components/WatchlistPanel";
import { ApiError, apiDelete, apiGet, apiPost } from "@/lib/api-client";
import { asArray, asRecord } from "@/lib/type-utils";

interface ExplorerFilters {
  search: string;
  category: string;
  limit: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

function asArrayFromPayload(input: unknown): Record<string, unknown>[] {
  const arr = asArray(input);
  if (arr.length > 0) return arr;
  if (input && typeof input === "object") {
    const record = input as Record<string, unknown>;
    if (Array.isArray(record.markets)) return asArray(record.markets);
    if (Array.isArray(record.items)) return asArray(record.items);
  }
  return [];
}

function getMarketId(market: Record<string, unknown>): string {
  const value = market.market_id ?? market.id;
  return typeof value === "string" ? value : "";
}

// Backend AddToWatchlistRequest requires price_at_add as a non-null float in
// [0, 1] — a null price is a guaranteed 422, so callers must guard on this.
function getMarketPrice(market: Record<string, unknown>): number | null {
  for (const value of [market.price, market.yes_price]) {
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function describeMutationError(error: unknown): string {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return "Admin login required to modify the watchlist.";
  }
  if (error instanceof Error && error.message) return error.message;
  return "Request failed";
}

export function MarketExplorerPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ExplorerFilters>({
    search: "",
    category: "",
    limit: 50,
    sortBy: "liquidity",
    sortOrder: "desc",
  });
  const [results, setResults] = useState<Record<string, unknown>[]>([]);
  const [selectedMarket, setSelectedMarket] = useState<Record<string, unknown> | null>(null);

  const categoriesQuery = useQuery({
    queryKey: ["explorer-categories"],
    queryFn: () => apiGet<unknown>("/api/explorer/categories"),
  });

  const exploreMutation = useMutation({
    mutationFn: () =>
      apiPost<unknown>("/api/markets/explore", {
        search: filters.search || undefined,
        category: filters.category || undefined,
        limit: filters.limit,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
      }),
    onSuccess: (payload) => {
      setResults(asArrayFromPayload(payload));
    },
  });

  const addWatchlistMutation = useMutation({
    mutationFn: async (market: Record<string, unknown>) => {
      const price = getMarketPrice(market);
      if (price === null) {
        throw new Error("Market has no price — cannot add to watchlist.");
      }
      const payload = await apiPost<unknown>("/api/watchlist", {
        market_id: market.market_id ?? market.id ?? "",
        question: market.question ?? market.title ?? "",
        price_at_add: price,
        volume_at_add: market.volume ?? null,
        liquidity_at_add: market.liquidity ?? null,
      });
      // The backend reports service failures as HTTP 200 + success:false —
      // treat that as an error so it is surfaced instead of swallowed.
      const record = asRecord(payload);
      if (record.success === false) {
        const message = typeof record.error === "string" && record.error ? record.error : "Failed to add market to watchlist.";
        throw new Error(message);
      }
      return payload;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
  });

  const removeWatchlistMutation = useMutation({
    mutationFn: (marketId: string) => apiDelete(`/api/watchlist/${encodeURIComponent(marketId)}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["watchlist"] });
    },
  });

  // GET /api/explorer/categories wraps the list: {total_markets, count, categories: [...]}
  const categories = asArray(asRecord(categoriesQuery.data).categories);
  const selectedMarketId = useMemo(
    () => (selectedMarket ? getMarketId(selectedMarket) : ""),
    [selectedMarket],
  );
  const selectedMarketPrice = selectedMarket ? getMarketPrice(selectedMarket) : null;

  return (
    <div className="space-y-4 md:space-y-6">
      <Panel
        title="Market Explorer"
        description="Run explorer filters, inspect result payloads, and maintain watchlist coverage."
      >
        <form
          className="grid grid-cols-1 gap-2 md:gap-3 sm:grid-cols-2 md:grid-cols-5"
          onSubmit={(event) => {
            event.preventDefault();
            exploreMutation.mutate();
          }}
        >
          <div className="md:col-span-2">
            <Label>Search</Label>
            <Input
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              placeholder="fed, election, crypto..."
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={filters.category}
              onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
            >
              <option value="">All categories</option>
              {categories.map((category, index) => {
                // Backend emits {id, label, slug, icon, count}; the explore
                // endpoint matches the slug, never the display label.
                const slug =
                  (typeof category.slug === "string" && category.slug) ||
                  (typeof category.id === "string" && category.id) ||
                  "";
                if (!slug) return null;
                const label = (typeof category.label === "string" && category.label) || slug;
                return (
                  <option key={`${slug}-${index}`} value={slug}>
                    {label}
                  </option>
                );
              })}
            </Select>
          </div>
          <div>
            <Label>Sort</Label>
            <Select
              value={filters.sortBy}
              onChange={(event) => setFilters((prev) => ({ ...prev, sortBy: event.target.value }))}
            >
              {/* Values must satisfy MarketExploreRequest.sort_by — anything else is a 422. */}
              <option value="liquidity">Liquidity</option>
              <option value="volume_24h">Volume (24h)</option>
              <option value="volume_total">Volume (total)</option>
              <option value="price_change_24h">Price change (24h)</option>
              <option value="end_date">End date</option>
              <option value="created_at">Created</option>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Input
              type="number"
              min={1}
              max={250}
              value={filters.limit}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, limit: Number(event.target.value) || 50 }))
              }
            />
            <Button type="submit" disabled={exploreMutation.isPending}>
              {exploreMutation.isPending ? "Running..." : "Explore"}
            </Button>
          </div>
        </form>
        {exploreMutation.isError ? (
          <p role="alert" className="mt-3 text-sm text-amber-300">
            Explore failed: {describeMutationError(exploreMutation.error)}
          </p>
        ) : null}
      </Panel>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <Panel
          title="Markets"
          description="Click a market to preload watchlist payload and inspect raw data."
        >
          <div className="max-h-[28rem] space-y-2 overflow-auto pr-1">
            {results.length === 0 ? (
              <p className="text-sm text-muted">Run explorer query to load markets.</p>
            ) : null}
            {results.map((market, index) => {
              const id = getMarketId(market);
              const question =
                (typeof market.question === "string" && market.question) ||
                (typeof market.title === "string" && market.title) ||
                `Market ${index + 1}`;
              const price =
                typeof market.price === "number"
                  ? market.price
                  : typeof market.yes_price === "number"
                    ? market.yes_price
                    : null;
              return (
                <button
                  key={`${id}-${index}`}
                  type="button"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-left hover:border-brand-500/40"
                  onClick={() => setSelectedMarket(market)}
                >
                  <div className="truncate text-sm font-medium text-text">{question}</div>
                  <div className="mt-1 text-xs text-muted">
                    {id ? `ID ${id}` : "No market id"} ·{" "}
                    {price !== null ? `Price ${price}` : "No price"}
                  </div>
                </button>
              );
            })}
          </div>
        </Panel>

        <Panel
          title="Selected Market"
          description="Add/remove selected market from watchlist and inspect payload."
          actions={
            <div className="flex gap-2">
              <Button
                disabled={
                  !selectedMarket || selectedMarketPrice === null || addWatchlistMutation.isPending
                }
                onClick={() => {
                  if (selectedMarket) addWatchlistMutation.mutate(selectedMarket);
                }}
              >
                Add to watchlist
              </Button>
              <Button
                variant="ghost"
                disabled={!selectedMarketId || removeWatchlistMutation.isPending}
                onClick={() => removeWatchlistMutation.mutate(selectedMarketId)}
              >
                Remove
              </Button>
            </div>
          }
        >
          {selectedMarket && selectedMarketPrice === null ? (
            <p className="mb-2 text-xs text-amber-300">
              No price on this market — adding to watchlist is disabled.
            </p>
          ) : null}
          {addWatchlistMutation.isError ? (
            <p role="alert" className="mb-2 text-xs text-amber-300">
              Add failed: {describeMutationError(addWatchlistMutation.error)}
            </p>
          ) : null}
          {removeWatchlistMutation.isError ? (
            <p role="alert" className="mb-2 text-xs text-amber-300">
              Remove failed: {describeMutationError(removeWatchlistMutation.error)}
            </p>
          ) : null}
          {selectedMarket ? (
            <div className="space-y-2 text-sm">
              <div className="text-text font-medium">{String(selectedMarket.question ?? selectedMarket.title ?? "Unknown market")}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted">Price:</span> <span className="text-text font-mono">{String(selectedMarket.yes_price ?? selectedMarket.price ?? "—")}</span></div>
                <div><span className="text-muted">Volume:</span> <span className="text-text font-mono">${Number(selectedMarket.volume_total ?? selectedMarket.volume ?? 0).toLocaleString()}</span></div>
                <div><span className="text-muted">Liquidity:</span> <span className="text-text font-mono">${Number(selectedMarket.liquidity ?? 0).toLocaleString()}</span></div>
                <div><span className="text-muted">End Date:</span> <span className="text-text">{String(selectedMarket.end_date ?? "—").substring(0, 10)}</span></div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted">Select a market first.</p>
          )}
        </Panel>
      </div>

      <div className="grid gap-3 md:gap-4 grid-cols-1 lg:grid-cols-2">
        <WatchlistPanel />
        {/* On-demand market lookup via the geoblock-free edge detail endpoint. */}
        <DecoderPanel />
      </div>
    </div>
  );
}
